import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import { useTranslations } from '../../../contexts/translation.context';
import { Typography } from '../../../components';
import { showPartnerSuccess, showError } from '../../../utils/toast';
import { FilterSelect } from '../../../components/ui';
import {
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  GlobalIc,
  IcReddit,
  IcAnotherSoc,
  IcTg,
  IcTikTok,
  IcYoutube,
  SearchInputIc,
  ResetFiltersIc,
  DangerIc,
} from '../../../components/icons';
import { staffApi, GetCPLOffersParams, CPLOffer } from '../../../api/staff';
import CPLOffersTable from './components/CPLOffersTable';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';

const CPLOffersPage: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.cplOffers'), isActive: true }
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  // State
  const [offers, setOffers] = useState<CPLOffer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [localSearch, setLocalSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<{
    status?: 'active' | 'hidden' | 'moderation' | 'deactivated' | 'all';
    source?: 'only_fans' | 'tiktok' | 'google' | 'instagram' | 'reddit' | 'telegram' | 'twitter' | 'youtube' | 'another' | 'all';
  }>({
    status: undefined,
    source: undefined,
  });

  // Status options - используем правильные значения из API
  const statusOptions = useMemo(() => [
    { key: 'all', label: t('admin.cplOffers.filters.status.all') },
    { key: 'active', label: 'Активный' },
    { key: 'deactivated', label: 'Деактивирован' },
    { key: 'moderation', label: 'На модерации' },
  ], [t]);

  // Source options - используем те же значения что и в Orders с иконками
  const sourceOptions = useMemo(() => [
    { key: 'all', label: t('admin.cplOffers.filters.source.all'), icon: React.createElement(GlobalIc, { size: 16 }) },
    { key: 'only_fans', label: 'OnlyFans', icon: React.createElement(OnlyFansIc, { size: 16, fill:'#A8A8B0' }) },
    { key: 'reddit', label: 'Reddit', icon: React.createElement(IcReddit, { size: 16 }) },
    { key: 'instagram', label: 'Instagram', icon: React.createElement(InstagramIc, { size: 16, fill:'#A8A8B0' }) },
    { key: 'tiktok', label: 'TikTok', icon: React.createElement(IcTikTok, { size: 16 }) },
    { key: 'google', label: 'Google', icon: React.createElement(GoogleIc, { size: 16, fill:'#A8A8B0' }) },
    { key: 'telegram', label: 'Telegram', icon: React.createElement(IcTg, { size: 16 }) },
    { key: 'twitter', label: 'Twitter', icon: React.createElement(TwitterIc, { size: 16, fill:'#A8A8B0' }) },
    { key: 'youtube', label: 'YouTube', icon: React.createElement(IcYoutube, { size: 16 }) },
    { key: 'another', label: t('admin.cplOffers.filters.source.other'), icon: React.createElement(IcAnotherSoc, { size: 16 }) },
  ], [t]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchValue !== '' ||
           (filters.status !== undefined && filters.status !== 'all') ||
           (filters.source !== undefined && filters.source !== 'all');
  }, [searchValue, filters]);

  // Fetch CPL offers
  const fetchOffers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: GetCPLOffersParams = {
        page: currentPage,
        page_size: pageSize,
        sort: '-created_at', // Sort by date descending (newest first)
        ...(searchValue && { search: searchValue }),
        ...(filters.status && filters.status !== 'all' && {
          status: filters.status as 'active' | 'hidden' | 'moderation' | 'deactivated'
        }),
        ...(filters.source && filters.source !== 'all' && {
          source: filters.source as 'only_fans' | 'tiktok' | 'google' | 'instagram' | 'reddit' | 'telegram' | 'twitter' | 'youtube' | 'another'
        }),
      };

      const response = await staffApi.getCPLOffers(params);
      setOffers(response.objects);
      setTotalCount(response.count);
    } catch (error: any) {
      console.error('Failed to load CPL offers:', error);

      // Показываем ошибку пользователю
      const errorMessage = error?.response?.data?.detail ||
                          error?.response?.data?.message ||
                          error?.message ||
                          'Не удалось загрузить офферы';

      showError(errorMessage);
      setOffers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchValue, filters.status, filters.source]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(localSearch);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Handlers
  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    if (key === 'status') {
      setFilters(prev => ({
        ...prev,
        status: value === 'all' ? undefined : value as 'active' | 'hidden' | 'moderation' | 'deactivated'
      }));
    } else if (key === 'source') {
      setFilters(prev => ({
        ...prev,
        source: value === 'all' ? undefined : value as 'only_fans' | 'tiktok' | 'google' | 'instagram' | 'reddit' | 'telegram' | 'twitter' | 'youtube' | 'another'
      }));
    }
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setLocalSearch('');
    setSearchValue('');
    setFilters({
      status: undefined,
      source: undefined,
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleStatusChange = useCallback(async (offerId: string, status: 'active' | 'deactivated') => {
    try {
      await staffApi.updateCPLOffer(offerId, { status });

      // Обновляем список офферов
      setOffers(prevOffers =>
        prevOffers.map(offer =>
          offer.id === offerId ? { ...offer, status } : offer
        )
      );

      showPartnerSuccess(
        status === 'active' ? 'Оффер успешно активирован' : 'Оффер успешно деактивирован'
      );
    } catch (err: any) {
      console.error('Error updating offer status:', err);

      // Извлекаем детальное сообщение об ошибке из ответа API
      const errorMessage = err?.response?.data?.detail ||
                          err?.response?.data?.message ||
                          err?.message ||
                          'Не удалось изменить статус оффера';

      showError(errorMessage);
    }
  }, []);

  const handleApproveOffer = useCallback(async (offerId: string) => {
    try {
      await staffApi.approveCPLOffer(offerId);

      // Обновляем список офферов
      setOffers(prevOffers =>
        prevOffers.map(offer =>
          offer.id === offerId ? { ...offer, status: 'active' } : offer
        )
      );

      showPartnerSuccess('Оффер успешно одобрен');
    } catch (err: any) {
      console.error('Error approving offer:', err);

      const errorMessage = err?.response?.data?.detail ||
                          err?.response?.data?.message ||
                          err?.message ||
                          'Не удалось одобрить оффер';

      showError(errorMessage);
    }
  }, []);

  const handleRejectOffer = useCallback(async (offerId: string) => {
    try {
      await staffApi.rejectCPLOffer(offerId);

      // Обновляем список офферов
      setOffers(prevOffers =>
        prevOffers.map(offer =>
          offer.id === offerId ? { ...offer, status: 'deactivated' } : offer
        )
      );

      showPartnerSuccess('Оффер успешно отклонен');
    } catch (err: any) {
      console.error('Error rejecting offer:', err);

      const errorMessage = err?.response?.data?.detail ||
                          err?.response?.data?.message ||
                          err?.message ||
                          'Не удалось отклонить оффер';

      showError(errorMessage);
    }
  }, []);

  return (
    <div className="h-screen flex flex-col">
      {/* Main Content */}
      <div className="flex-1">
        <div className="px-[24px] py-[24px]">
          {/* Title and Filters */}
          <div className="mb-[16px]">
            <Typography variant="h5" className="text-[#11181C] font-semibold mb-[16px]">
              {t('admin.cplOffers.title')}
            </Typography>

            {/* Filters Row */}
            <div className="flex items-center gap-[16px] flex-wrap">
              {/* Search Input */}
              <Input
                type="text"
                placeholder="Имя/ID"
                size="sm"
                value={localSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                endContent={<SearchInputIc />}
                classNames={{
                  mainWrapper: "w-[273px]",
                  base: "w-[273px]",
                  input: "text-sm",
                  inputWrapper: "rounded-lg h-[36px] w-[273px]",
                }}
              />

              {/* Source Filter */}
              <FilterSelect
                selectedKey={filters.source || 'all'}
                onSelectionChange={(value) => handleFilterChange('source', value)}
                options={sourceOptions}
                placeholder={t('admin.cplOffers.filters.source.placeholder')}
                variant="campaign"
                autoWidth={true}
              />

              {/* Status Filter */}
              <FilterSelect
                selectedKey={filters.status || 'all'}
                onSelectionChange={(value) => handleFilterChange('status', value)}
                options={statusOptions}
                placeholder={t('admin.cplOffers.filters.status.placeholder')}
                variant="campaign"
                autoWidth={true}
                startContent={<DangerIc size={20}/>}
              />

              {/* Reset Filters Button */}
              {hasActiveFilters && (
                <Tooltip content={t('admin.cplOffers.filters.reset')} placement="top">
                  <Button
                    size="sm"
                    className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
                    onPress={handleResetFilters}
                  >
                    <ResetFiltersIc/>
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
          <div >
            <CPLOffersTable
              offers={offers}
              isLoading={isLoading}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onStatusChange={handleStatusChange}
              onApproveOffer={handleApproveOffer}
              onRejectOffer={handleRejectOffer}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CPLOffersPage;
